import {Expose, instanceToPlain, Transform} from 'class-transformer';
import {IsArray, IsBoolean, IsEmail, IsOptional, IsPhoneNumber, IsString, IsUUID} from 'class-validator';
import {UserRoleType} from '../types/user-role.type';
import {UserComputedProperties} from './user-computed.entity';
import {MinimalUserPreferences, UserPreferences} from './user-preferences.entity';

export class UserModel extends UserComputedProperties {
	@Expose() @IsString() @IsUUID() id = '';
	@Expose() @IsString() username: string = '';
	@Expose() @IsString() fullname: string = '';
	@Expose() @IsString() title: string = '';
	@Expose() @IsString() company: string = '';
	@Expose() @IsString() department: string = '';
	@Expose() @IsString() notes: string = '';
	@Expose() @IsBoolean() enabled: boolean = true;
	@Expose() @IsString() @IsEmail() email: string = '';
	@Expose() @IsString() @IsPhoneNumber('IL') phonenumber: string = '';
	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) hasPortrait: boolean = false;
	@Expose() @IsBoolean() @Transform(({value}) => value ?? false) hide: boolean = false;
	@Expose() @Transform(({value}) => value ?? null) preferences?: UserPreferences;
	@Expose() roles?: UserRoleType[] = [];
	@Expose() @IsArray() @IsString({each: true}) tags?: string[] = [];

	constructor(init?: Partial<UserModel>) {
		super();
		this.update(init ?? {});
	}

	public static fromIUser(user: Partial<UserModel>): UserModel {
		return new UserModel({
			id: user.id,
			username: user.username,
			fullname: user.fullname,
			title: user.title,
			email: user.email,
			phonenumber: user.phonenumber,
			company: user.company,
			department: user.department,
			notes: user.notes,
			enabled: user.enabled,
			hasPortrait: user.hasPortrait,
			hide: user.hide,
			tags: user.tags,
			roles: user.roles,
			preferences: UserPreferences.fromIUserPreferences(user.preferences),
		});
	}

	public update(updates: DeepPartial<UserModel>): void {
		this.id = updates.id ?? this.id;
		this.username = updates.username ?? this.username;
		this.fullname = updates.fullname ?? this.fullname;
		this.title = updates.title ?? this.title;
		this.email = updates.email ?? this.email;
		this.phonenumber = this.adaptPhoneNumber(updates.phonenumber) ?? this.phonenumber;
		this.company = updates.company ?? this.company;
		this.department = updates.department ?? this.department;
		this.notes = updates.notes ?? this.notes;
		this.enabled = updates.enabled ?? this.enabled;
		this.hasPortrait = updates.hasPortrait ?? this.hasPortrait;
		this.hide = updates.hide ?? this.hide;
		this.roles = updates.roles ?? this.roles;
		this.tags = updates.tags ?? this.tags;
		if (updates.preferences) {
			if (!this.preferences) {
				this.preferences = new UserPreferences(updates.preferences);
			} else {
				this.preferences.update(updates.preferences);
			}
		} else {
			this.preferences = new UserPreferences();
		}
		this.computeProperties(this);
	}

	public static isUserModel(user: any): user is UserModel {
		return 'roles' in user && 'preferences' in user && UserPreferences.isUserPreferences(user.preferences);
	}

	private adaptPhoneNumber(phoneNumber: string): string {
		if (!phoneNumber) return null;
		// take only the digits
		const digits = phoneNumber.replace(/\D/g, '');
		return /^\d{10}$/.test(digits) ? digits : null;
	}

	toJSON() {
		return instanceToPlain(this);
	}
}

export class MinimalUserModel
	extends UserComputedProperties
	implements Pick<UserModel, 'id' | 'username' | 'fullname' | 'title' | 'company' | 'department' | 'notes' | 'email' | 'phonenumber' | 'hasPortrait'>
{
	@IsString() @IsUUID() id = '';
	@IsString() @IsOptional() username: string;
	@IsString() @IsOptional() fullname: string;
	@IsString() @IsOptional() title: string;
	@IsString() @IsOptional() company: string;
	@IsString() @IsOptional() department: string;
	@IsString() @IsOptional() notes: string;
	@IsBoolean() @IsOptional() enabled: boolean;
	@IsString() @IsOptional() @IsEmail() email: string;
	@IsString() @IsPhoneNumber('IL') phonenumber: string;
	@IsBoolean() @IsOptional() @Transform(({value}) => value ?? false) hasPortrait: boolean;
	@IsOptional() @Transform(({value}) => value ?? null) preferences?: MinimalUserPreferences;

	constructor(init?: Partial<MinimalUserModel>) {
		super();
		Object.assign(this, init ?? {});
		this.computeProperties(this);
	}

	public static isMinimalUserModel(user: any): user is MinimalUserModel {
		return 'preferences' in user && MinimalUserPreferences.isMinimalUserPreferences(user.preferences);
	}
}
